import { useState, useRef, useEffect } from 'react'
import { Card } from '../../../components/ui/Card'
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Loader2, Download, X, Package, GitBranch, Search, Filter, ChevronLeft, ChevronRight, PackageCheck } from 'lucide-react'
import { api } from '../../../lib/api'
import { useLanguage } from '../../../context/LanguageContext'
import { useFactory } from '../../../context/FactoryContext'
import * as XLSX from 'xlsx'
import SalaMezclaGestion from './components/SalaMezclaGestion'

const MOVEMENT_TYPES = [
  { value: '', label: 'Todos' },
  { value: 101, label: 'Alta Producción' },
  { value: 102, label: 'Anula Alta Producción' },
  { value: 261, label: 'Consumo Materias Primas' },
  { value: 262, label: 'Anula Consumo' },
  { value: 311, label: 'Traslado' },
  { value: 531, label: 'Altas de Scrap' },
  { value: 532, label: 'Anula Scrap' },
  { value: 601, label: 'Salida Venta' },
  { value: 602, label: 'Devolución Venta' },
  { value: 711, label: 'Alta Inventario' },
  { value: 712, label: 'Baja Inventario' }
]

// Plan trazabilidad: Materia Prima → Producción → Scrap → Residuo/Waste
const TRACEABILITY_LEGEND = [
  { key: 'consumo', label: '← Consumo (261)', desc: 'Materia prima / insumos', color: 'bg-red-500/20 text-red-400 border-red-500/40' },
  { key: 'produccion', label: '→ Producción (101)', desc: 'Altas de producto', color: 'bg-green-500/20 text-green-400 border-green-500/40' },
  { key: 'scrap', label: '⚠ Scrap (531)', desc: 'Recuperable / reproceso', color: 'bg-amber-500/20 text-amber-400 border-amber-500/40' },
  { key: 'waste', label: '⊕ Residuo', desc: 'WA - sale del ciclo', color: 'bg-gray-600 text-gray-300 border-gray-500' }
]

const MATERIAL_TYPE_PREFIXES = ['RM', 'RE', 'EX', 'TH', 'TE', 'TI', 'RN', 'WA']
const MATERIAL_TYPE_LABELS = { RM: 'MP', RE: 'RE', EX: 'EX', TH: 'TH', TE: 'TE', TI: 'TI', RN: 'RN', WA: 'WA' }

function getMaterialType(code) {
  if (!code || typeof code !== 'string') return null
  const up = code.toUpperCase()
  for (const p of MATERIAL_TYPE_PREFIXES) {
    if (up.startsWith(p)) return p
  }
  return null
}

function getMovementConcept(movementType) {
  if (!movementType) return 'other'
  const t = String(movementType)
  if (t.includes('Consumo') || t.includes('Anula Consumo')) return 'consumo'
  if (t.includes('Alta') && t.includes('Producción')) return 'produccion'
  if (t.includes('Scrap')) return 'scrap'
  if (t.includes('Residuo') || t.includes('Baja') || t.includes('Pérdida')) return 'waste'
  return 'other'
}

function getMovementBadgeClass(movementType) {
  const concept = getMovementConcept(movementType)
  const map = { consumo: 'bg-red-500/20 text-red-400 border border-red-500/40', produccion: 'bg-green-500/20 text-green-400 border border-green-500/40', scrap: 'bg-amber-500/20 text-amber-400 border border-amber-500/40', waste: 'bg-gray-600 text-gray-300 border border-gray-500', other: 'bg-gray-700 text-gray-300 border border-gray-600' }
  return map[concept] || map.other
}

export default function LotesTrazabilidad() {
  const { t } = useLanguage()
  const { selectedFactory } = useFactory()
  const defaultFactory = (selectedFactory && selectedFactory !== 'CX') ? selectedFactory : ''

  const [file, setFile] = useState(null)
  const [data, setData] = useState([])
  const [columns, setColumns] = useState([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })
  const [previewData, setPreviewData] = useState([])
  const fileInputRef = useRef(null)
  
  const PAGE_SIZE = 20

  const [activeTab, setActiveTab] = useState('import')
  const [batches, setBatches] = useState([])
  const [traceability, setTraceability] = useState([])
  const [loadingBatches, setLoadingBatches] = useState(false)
  const [loadingTraceability, setLoadingTraceability] = useState(false)
  const [batchesTotal, setBatchesTotal] = useState(0)
  const [traceabilityTotal, setTraceabilityTotal] = useState(0)
  const [batchesPage, setBatchesPage] = useState(1)
  const [traceabilityPage, setTraceabilityPage] = useState(1)
  
  const [filters, setFilters] = useState({
    factory: defaultFactory,
    materialCode: '',
    batchNumber: '',
    orderNumber: '',
    movementType: '',
    year: '',
    month: '',
    day: ''
  })

  useEffect(() => {
    const nextFactory = (selectedFactory && selectedFactory !== 'CX') ? selectedFactory : ''
    setFilters(prev => (prev.factory !== nextFactory ? { ...prev, factory: nextFactory } : prev))
  }, [selectedFactory])

  const filtersRef = useRef(filters)
  filtersRef.current = filters

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0]
    if (selectedFile) {
      setFile(selectedFile)
      setMessage({ type: '', text: '' })
      processExcelFile(selectedFile)
    }
  }

  const processExcelFile = (file) => {
    setLoading(true)
    const reader = new FileReader()
    
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'binary' })
        const firstSheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[firstSheetName]
        
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
          header: 1,
          defval: ''
        })
        
        if (jsonData.length === 0) {
          setMessage({ type: 'error', text: 'El archivo Excel está vacío' })
          setLoading(false)
          return
        }
        
        const headers = jsonData[0].map(h => String(h).trim()).filter(h => h)
        const rows = jsonData.slice(1).filter(row => row.some(cell => cell !== ''))
        
        setColumns(headers)
        
        const processedData = rows.map((row, index) => {
          const obj = { _rowIndex: index + 2 }
          headers.forEach((header, colIndex) => {
            obj[header] = row[colIndex] !== undefined ? String(row[colIndex]).trim() : ''
          })
          return obj
        })
        
        setData(processedData)
        setPreviewData(processedData.slice(0, 10))
        setMessage({ 
          type: 'success', 
          text: `Archivo procesado: ${processedData.length} registros encontrados, ${headers.length} columnas` 
        })
      } catch (error) {
        console.error('Error processing Excel:', error)
        setMessage({ type: 'error', text: `Error al procesar el archivo: ${error.message}` })
      } finally {
        setLoading(false)
      }
    }
    
    reader.onerror = () => {
      setMessage({ type: 'error', text: 'Error al leer el archivo' })
      setLoading(false)
    }
    
    reader.readAsBinaryString(file)
  }

  const handleUpload = async () => {
    if (!data || data.length === 0) {
      setMessage({ type: 'error', text: 'No hay datos para subir' })
      return
    }

    setUploading(true)
    setMessage({ type: '', text: '' })

    try {
      const result = await api.post('/api/batch/import', {
        data: data,
        columns: columns,
        filename: file?.name || 'mb51.xlsx'
      })

      setMessage({ 
        type: 'success', 
        text: `✅ ${result.message || `Se importaron ${result.count || data.length} registros exitosamente`}` 
      })
      
      if (activeTab === 'batches') {
        loadBatches()
      } else if (activeTab === 'traceability') {
        loadTraceability()
      }
      
      setTimeout(() => {
        setFile(null)
        setData([])
        setPreviewData([])
        setColumns([])
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }, 3000)
    } catch (error) {
      console.error('Error uploading data:', error)
      setMessage({ 
        type: 'error', 
        text: `Error al subir los datos: ${error.message}` 
      })
    } finally {
      setUploading(false)
    }
  }

  const handleClear = () => {
    setFile(null)
    setData([])
    setPreviewData([])
    setColumns([])
    setMessage({ type: '', text: '' })
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const loadBatches = async (page = batchesPage) => {
    const f = filtersRef.current
    setLoadingBatches(true)
    try {
      const params = new URLSearchParams()
      params.append('limit', PAGE_SIZE)
      params.append('offset', (page - 1) * PAGE_SIZE)
      if (f.factory) params.append('factory', f.factory)
      if (f.materialCode) params.append('material', f.materialCode)
      if (f.batchNumber) params.append('batch', f.batchNumber)
      if (f.orderNumber) params.append('order', f.orderNumber)
      if (f.movementType) params.append('movement_type', f.movementType)
      if (f.year) params.append('year', f.year)
      if (f.month) params.append('month', f.month)
      if (f.day) params.append('day', f.day)

      const response = await api.get(`/api/batch/list?${params.toString()}`)
      const data = response?.data ?? response
      const items = Array.isArray(data) ? data : (data?.data ?? [])
      setBatches(items)
      setBatchesTotal(response?.total ?? data?.total ?? items.length)
      setBatchesPage(page)
    } catch (error) {
      console.error('Error loading batches:', error)
      setMessage({ type: 'error', text: `Error al cargar lotes: ${error.message}` })
    } finally {
      setLoadingBatches(false)
    }
  }

  const loadTraceability = async (page = traceabilityPage) => {
    const f = filtersRef.current
    setLoadingTraceability(true)
    try {
      const params = new URLSearchParams()
      params.append('limit', PAGE_SIZE)
      params.append('offset', (page - 1) * PAGE_SIZE)
      if (f.factory) params.append('factory', f.factory)
      if (f.materialCode) params.append('material', f.materialCode)
      if (f.movementType) params.append('movement_type', f.movementType)
      if (f.year) params.append('year', f.year)
      if (f.month) params.append('month', f.month)
      if (f.day) params.append('day', f.day)

      const response = await api.get(`/api/batch/traceability?${params.toString()}`)
      const data = response?.data ?? response
      const items = Array.isArray(data) ? data : (data?.data ?? [])
      setTraceability(items)
      setTraceabilityTotal(response?.total ?? data?.total ?? items.length)
      setTraceabilityPage(page)
    } catch (error) {
      console.error('Error loading traceability:', error)
      setMessage({ type: 'error', text: `Error al cargar trazabilidad: ${error.message}` })
    } finally {
      setLoadingTraceability(false)
    }
  }

  // Cargar datos al cambiar de pestaña (siempre página 1)
  useEffect(() => {
    if (activeTab === 'batches') {
      loadBatches(1)
    } else if (activeTab === 'traceability') {
      loadTraceability(1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab])

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('es-ES', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return dateString
    }
  }

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
              <FileSpreadsheet className="w-8 h-8 text-blue-400" />
              {t.produccion?.lotesTrazabilidad?.title || 'Lotes y Trazabilidad'}
            </h1>
            <p className="text-gray-400">
              {t.produccion?.lotesTrazabilidad?.description || 'Importa y visualiza datos de lotes y trazabilidad desde archivos Excel (MB51)'}
            </p>
          </div>
        </div>

        <div className="flex gap-2 border-b border-gray-700">
          <button
            onClick={() => setActiveTab('import')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'import'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            <Upload className="w-4 h-4 inline mr-2" />
            {t.common?.import || 'Importar'}
          </button>
          <button
            onClick={() => setActiveTab('batches')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'batches'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            <Package className="w-4 h-4 inline mr-2" />
            {t.produccion?.lotesTrazabilidad?.batches || 'Lotes'} ({batchesTotal})
          </button>
          <button
            onClick={() => setActiveTab('traceability')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'traceability'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            <GitBranch className="w-4 h-4 inline mr-2" />
            {t.produccion?.lotesTrazabilidad?.traceability || 'Trazabilidad'} ({traceabilityTotal})
          </button>
          <button
            onClick={() => setActiveTab('sala-mezcla')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'sala-mezcla'
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            <PackageCheck className="w-4 h-4 inline mr-2" />
            {t.produccion?.lotesTrazabilidad?.salaMezcla || 'Sala Mezcla'}
          </button>
        </div>

        {activeTab === 'import' && (
          <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <label className="flex-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <div className="flex items-center gap-3 p-4 border-2 border-dashed border-gray-600 rounded-lg hover:border-blue-500 cursor-pointer transition-colors">
                  <Upload className="w-6 h-6 text-gray-400" />
                  <div className="flex-1">
                    <span className="text-white font-medium">
                      {file ? file.name : 'Seleccionar archivo Excel (.xlsx, .xls)'}
                    </span>
                    <p className="text-sm text-gray-400 mt-1">
                      {file ? 'Click para cambiar el archivo' : 'Click para seleccionar un archivo'}
                    </p>
                  </div>
                </div>
              </label>
              {file && (
                <button
                  onClick={handleClear}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  {t.common?.clear || 'Limpiar'}
                </button>
              )}
            </div>

            {loading && (
              <div className="flex items-center gap-2 text-blue-400">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>{t.common?.processing || 'Procesando archivo...'}</span>
              </div>
            )}

            {message.text && (
              <div className={`p-4 rounded-lg flex items-center gap-2 ${
                message.type === 'success' 
                  ? 'bg-green-500/10 border border-green-500/50 text-green-400'
                  : 'bg-red-500/10 border border-red-500/50 text-red-400'
              }`}>
                {message.type === 'success' ? (
                  <CheckCircle2 className="w-5 h-5" />
                ) : (
                  <AlertCircle className="w-5 h-5" />
                )}
                <span>{message.text}</span>
              </div>
            )}

            {data.length > 0 && (
              <div className="flex gap-3">
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t.common?.uploading || 'Subiendo...'}
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      {t.common?.import || 'Importar'} {data.length} {t.common?.records || 'registros'}
                    </>
                  )}
                </button>
                <div className="px-4 py-2 bg-gray-800 rounded-lg text-gray-300 flex items-center gap-2">
                  <FileSpreadsheet className="w-4 h-4" />
                  <span>{data.length} {t.common?.records || 'registros'} • {columns.length} {t.common?.columns || 'columnas'}</span>
                </div>
              </div>
            )}
          </div>
        </Card>
        )}

        {activeTab === 'import' && previewData.length > 0 && (
          <Card className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-white">
                {t.common?.preview || 'Vista Previa'} ({previewData.length} {t.common?.of || 'de'} {data.length} {t.common?.records || 'registros'})
              </h2>
              {previewData.length < data.length && (
                <span className="text-sm text-gray-400">
                  {t.common?.showingFirst || 'Mostrando primeras'} {previewData.length} {t.common?.rows || 'filas'}
                </span>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-800">
                    {columns.map((col, idx) => (
                      <th
                        key={idx}
                        className="px-4 py-3 text-left text-sm font-semibold text-gray-300 border-b border-gray-700"
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((row, rowIdx) => (
                    <tr
                      key={rowIdx}
                      className="border-b border-gray-700 hover:bg-gray-800/50 transition-colors"
                    >
                      {columns.map((col, colIdx) => (
                        <td
                          key={colIdx}
                          className="px-4 py-3 text-sm text-gray-300"
                        >
                          {row[col] || '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {activeTab === 'batches' && (
          <div className="space-y-4">
            <Card className="p-4">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Filter className="w-5 h-5 text-gray-400" />
                  <h3 className="text-lg font-semibold text-white">{t.common?.filters || 'Filtros'}</h3>
                </div>
                <button
                  onClick={() => loadBatches(1)}
                  disabled={loadingBatches}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  {loadingBatches ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                  {t.common?.search || 'Buscar'}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t.common?.factory || 'Fábrica'}</label>
                  <input
                    type="text"
                    value={filters.factory}
                    onChange={(e) => setFilters({ ...filters, factory: e.target.value })}
                    placeholder="Ej: CXC"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t.produccion?.lotesTrazabilidad?.materialCode || 'Código Material'}</label>
                  <input
                    type="text"
                    value={filters.materialCode}
                    onChange={(e) => setFilters({ ...filters, materialCode: e.target.value })}
                    placeholder={t.produccion?.lotesTrazabilidad?.materialCode || 'Código material'}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t.produccion?.lotesTrazabilidad?.batchNumber || 'Número de Lote'}</label>
                  <input
                    type="text"
                    value={filters.batchNumber}
                    onChange={(e) => setFilters({ ...filters, batchNumber: e.target.value })}
                    placeholder={t.produccion?.lotesTrazabilidad?.batchNumber || 'Número de lote'}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t.produccion?.lotesTrazabilidad?.orderNumber || 'Orden de Fabricación'}</label>
                  <input
                    type="text"
                    value={filters.orderNumber}
                    onChange={(e) => setFilters({ ...filters, orderNumber: e.target.value })}
                    placeholder={t.produccion?.lotesTrazabilidad?.orderNumber || 'Orden'}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t.produccion?.lotesTrazabilidad?.movementType || 'Tipo de Movimiento'}</label>
                  <select
                    value={filters.movementType}
                    onChange={(e) => setFilters({ ...filters, movementType: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  >
                    {MOVEMENT_TYPES.map((m) => (
                      <option key={m.value || 'all'} value={String(m.value)}>{m.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t.common?.year || 'Año'}</label>
                  <input
                    type="number"
                    min="2000"
                    max="2100"
                    value={filters.year}
                    onChange={(e) => setFilters({ ...filters, year: e.target.value })}
                    placeholder="Ej: 2025"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t.common?.month || 'Mes'}</label>
                  <input
                    type="number"
                    min="1"
                    max="12"
                    value={filters.month}
                    onChange={(e) => setFilters({ ...filters, month: e.target.value })}
                    placeholder="1-12"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t.common?.day || 'Día'}</label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={filters.day}
                    onChange={(e) => setFilters({ ...filters, day: e.target.value })}
                    placeholder="1-31"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h2 className="text-xl font-semibold text-white">{t.produccion?.lotesTrazabilidad?.batches || 'Lotes'}</h2>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-400">
                    {batchesTotal > 0
                      ? `${t.common?.showing || 'Mostrando'} ${(batchesPage - 1) * PAGE_SIZE + 1}-${Math.min(batchesPage * PAGE_SIZE, batchesTotal)} ${t.common?.of || 'de'} ${batchesTotal}`
                      : `0 ${t.common?.records || 'registros'}`
                    }
                  </span>
                  <button
                    onClick={() => loadBatches(batchesPage)}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Loader2 className={`w-4 h-4 ${loadingBatches ? 'animate-spin' : ''}`} />
                    {t.common?.refresh || 'Actualizar'}
                  </button>
                </div>
              </div>
              
              {loadingBatches ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
                </div>
              ) : batches.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{t.common?.noData || 'No hay lotes disponibles'}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-800">
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 border-b border-gray-700">{t.produccion?.lotesTrazabilidad?.batch || 'Lote'}</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 border-b border-gray-700">{t.produccion?.lotesTrazabilidad?.material || 'Material'}</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 border-b border-gray-700">{t.common?.description || 'Descripción'}</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 border-b border-gray-700">{t.common?.quantity || 'Cantidad'}</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 border-b border-gray-700">{t.common?.unit || 'Unidad'}</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 border-b border-gray-700">{t.produccion?.lotesTrazabilidad?.movementType || 'Tipo Movimiento'}</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 border-b border-gray-700">{t.common?.factory || 'Fábrica'}</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 border-b border-gray-700">{t.produccion?.lotesTrazabilidad?.orderNumber || 'Orden Fabricación'}</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 border-b border-gray-700">{t.common?.date || 'Fecha'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {batches.map((batch, index) => (
                        <tr key={batch.ID != null ? `batch-${batch.ID}-${index}` : `batch-${index}`} className="border-b border-gray-700 hover:bg-gray-800/50 transition-colors">
                          <td className="px-4 py-3 text-sm text-gray-300">{batch.BatchNumber || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-300 font-mono">{batch.MaterialCode || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-300">{batch.MaterialName || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-300">{batch.Quantity || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-300">{batch.Unit || '-'}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded ${
                              batch.MovementType?.includes('Alta') 
                                ? 'bg-green-500/20 text-green-400' 
                                : batch.MovementType?.includes('Eliminación')
                                ? 'bg-red-500/20 text-red-400'
                                : 'bg-gray-700 text-gray-300'
                            }`}>
                              {batch.MovementType || '-'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-300 font-semibold">{batch.Factory || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-300 font-mono">{batch.OrderNumber || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-400">{formatDate(batch.DocumentDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {batches.length > 0 && batchesTotal > PAGE_SIZE && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
                  <span className="text-sm text-gray-400">
                    {t.common?.page || 'Página'} {batchesPage} {t.common?.of || 'de'} {Math.ceil(batchesTotal / PAGE_SIZE)}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => loadBatches(batchesPage - 1)}
                      disabled={batchesPage <= 1 || loadingBatches}
                      className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg flex items-center gap-1"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      {t.common?.previous || 'Anterior'}
                    </button>
                    <button
                      onClick={() => loadBatches(batchesPage + 1)}
                      disabled={batchesPage >= Math.ceil(batchesTotal / PAGE_SIZE) || loadingBatches}
                      className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg flex items-center gap-1"
                    >
                      {t.common?.next || 'Siguiente'}
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}

        {activeTab === 'traceability' && (
          <div className="space-y-4">
            <Card className="p-4">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Filter className="w-5 h-5 text-gray-400" />
                  <h3 className="text-lg font-semibold text-white">{t.common?.filters || 'Filtros'}</h3>
                </div>
                <button
                  onClick={() => loadTraceability(1)}
                  disabled={loadingTraceability}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  {loadingTraceability ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                  {t.common?.search || 'Buscar'}
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t.common?.factory || 'Fábrica'}</label>
                  <input
                    type="text"
                    value={filters.factory}
                    onChange={(e) => setFilters({ ...filters, factory: e.target.value })}
                    placeholder="Ej: CXC"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t.produccion?.lotesTrazabilidad?.materialCode || 'Código Material'}</label>
                  <input
                    type="text"
                    value={filters.materialCode}
                    onChange={(e) => setFilters({ ...filters, materialCode: e.target.value })}
                    placeholder={t.produccion?.lotesTrazabilidad?.materialCode || 'Código material'}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t.produccion?.lotesTrazabilidad?.movementType || 'Tipo de Movimiento'}</label>
                  <select
                    value={filters.movementType}
                    onChange={(e) => setFilters({ ...filters, movementType: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  >
                    {MOVEMENT_TYPES.map((m) => (
                      <option key={m.value || 'all'} value={String(m.value)}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t.common?.year || 'Año'}</label>
                  <input
                    type="number"
                    min="2000"
                    max="2100"
                    value={filters.year}
                    onChange={(e) => setFilters({ ...filters, year: e.target.value })}
                    placeholder="Ej: 2025"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t.common?.month || 'Mes'}</label>
                  <input
                    type="number"
                    min="1"
                    max="12"
                    value={filters.month}
                    onChange={(e) => setFilters({ ...filters, month: e.target.value })}
                    placeholder="1-12"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">{t.common?.day || 'Día'}</label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={filters.day}
                    onChange={(e) => setFilters({ ...filters, day: e.target.value })}
                    placeholder="1-31"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h2 className="text-xl font-semibold text-white">{t.produccion?.lotesTrazabilidad?.traceability || 'Trazabilidad'}</h2>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-400">
                    {traceabilityTotal > 0
                      ? `${t.common?.showing || 'Mostrando'} ${(traceabilityPage - 1) * PAGE_SIZE + 1}-${Math.min(traceabilityPage * PAGE_SIZE, traceabilityTotal)} ${t.common?.of || 'de'} ${traceabilityTotal}`
                      : `0 ${t.common?.records || 'registros'}`
                    }
                  </span>
                  <button
                    onClick={() => loadTraceability(traceabilityPage)}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Loader2 className={`w-4 h-4 ${loadingTraceability ? 'animate-spin' : ''}`} />
                    {t.common?.refresh || 'Actualizar'}
                  </button>
                </div>
              </div>

              {/* Leyenda del plan: Materia Prima → Producción → Scrap → Residuo */}
              <div className="mb-4 p-4 rounded-lg bg-gray-800/50 border border-gray-700">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Flujo de trazabilidad</div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {TRACEABILITY_LEGEND.map((item) => (
                    <div key={item.key} className={`px-3 py-1.5 rounded-lg border text-sm font-medium ${item.color}`}>
                      {item.label}
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                  <span>RM (Materia Prima)</span>
                  <span>RE (Recuperado)</span>
                  <span>EX (Bobina)</span>
                  <span>TH (Termoformado)</span>
                  <span>TE (Etiquetado)</span>
                  <span>RN (Scrap reprocesable)</span>
                  <span>WA (Residuo)</span>
                </div>
              </div>

              {/* Filtros rápidos por concepto */}
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-sm text-gray-400 self-center mr-1">Filtrar:</span>
                <button
                  onClick={() => {
                    const val = filters.movementType === '261' ? '' : '261'
                    filtersRef.current = { ...filtersRef.current, movementType: val }
                    setFilters(prev => ({ ...prev, movementType: val }))
                    loadTraceability(1)
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filters.movementType === '261' ? 'bg-red-500/30 text-red-400 border border-red-500/50' : 'bg-gray-700 text-gray-400 hover:bg-gray-600 border border-gray-600'}`}
                >
                  Consumo (261)
                </button>
                <button
                  onClick={() => {
                    const val = filters.movementType === '101' ? '' : '101'
                    filtersRef.current = { ...filtersRef.current, movementType: val }
                    setFilters(prev => ({ ...prev, movementType: val }))
                    loadTraceability(1)
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filters.movementType === '101' ? 'bg-green-500/30 text-green-400 border border-green-500/50' : 'bg-gray-700 text-gray-400 hover:bg-gray-600 border border-gray-600'}`}
                >
                  Producción (101)
                </button>
                <button
                  onClick={() => {
                    const val = filters.movementType === '531' ? '' : '531'
                    filtersRef.current = { ...filtersRef.current, movementType: val }
                    setFilters(prev => ({ ...prev, movementType: val }))
                    loadTraceability(1)
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filters.movementType === '531' ? 'bg-amber-500/30 text-amber-400 border border-amber-500/50' : 'bg-gray-700 text-gray-400 hover:bg-gray-600 border border-gray-600'}`}
                >
                  Scrap (531)
                </button>
                <button
                  onClick={() => {
                    filtersRef.current = { ...filtersRef.current, movementType: '' }
                    setFilters(prev => ({ ...prev, movementType: '' }))
                    loadTraceability(1)
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${!filters.movementType ? 'bg-blue-500/20 text-blue-400 border border-blue-500/40' : 'bg-gray-700 text-gray-400 hover:bg-gray-600 border border-gray-600'}`}
                >
                  Todos
                </button>
              </div>
              
              {loadingTraceability ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
                </div>
              ) : traceability.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <GitBranch className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{t.common?.noData || 'No hay datos de trazabilidad disponibles'}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-800">
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 border-b border-gray-700">{t.produccion?.lotesTrazabilidad?.material || 'Material'}</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 border-b border-gray-700">{t.common?.description || 'Descripción'}</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 border-b border-gray-700">{t.common?.quantity || 'Cantidad'}</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 border-b border-gray-700">{t.produccion?.lotesTrazabilidad?.movementType || 'Tipo Movimiento'}</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 border-b border-gray-700">{t.common?.factory || 'Fábrica'}</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 border-b border-gray-700">{t.produccion?.lotesTrazabilidad?.orderNumber || 'Orden Fabricación'}</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 border-b border-gray-700">{t.produccion?.lotesTrazabilidad?.sourceBatch || 'Lote Origen'}</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 border-b border-gray-700">{t.produccion?.lotesTrazabilidad?.destinationBatch || 'Lote Destino'}</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300 border-b border-gray-700">{t.common?.date || 'Fecha'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {traceability.map((trace, index) => {
                        const matType = getMaterialType(trace.MaterialCode)
                        const qty = trace.Quantity
                        const isNegative = typeof qty === 'number' ? qty < 0 : (parseFloat(qty) || 0) < 0
                        return (
                        <tr key={trace.ID != null ? `trace-${trace.ID}-${index}` : `trace-${index}`} className="border-b border-gray-700 hover:bg-gray-800/50 transition-colors">
                          <td className="px-4 py-3 text-sm">
                            <div className="flex items-center gap-2">
                              {matType && (
                                <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-gray-700 text-gray-300 font-mono">
                                  {MATERIAL_TYPE_LABELS[matType] || matType}
                                </span>
                              )}
                              <span className="text-gray-300 font-mono">{trace.MaterialCode || '-'}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-300">{trace.MaterialName || '-'}</td>
                          <td className={`px-4 py-3 text-sm font-mono font-medium ${isNegative ? 'text-red-400' : 'text-green-400'}`}>
                            {typeof qty === 'number' ? qty : (trace.Quantity ?? '-')}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded border ${getMovementBadgeClass(trace.MovementType)}`}>
                              {trace.MovementType || '-'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-300 font-semibold">{trace.Factory || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-300 font-mono">{trace.OrderNumber || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-300">{trace.SourceBatch || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-300">{trace.DestinationBatch || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-400">{formatDate(trace.DocumentDate)}</td>
                        </tr>
                      )})}
                    </tbody>
                  </table>
                </div>
              )}
              {traceability.length > 0 && traceabilityTotal > PAGE_SIZE && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
                  <span className="text-sm text-gray-400">
                    {t.common?.page || 'Página'} {traceabilityPage} {t.common?.of || 'de'} {Math.ceil(traceabilityTotal / PAGE_SIZE)}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => loadTraceability(traceabilityPage - 1)}
                      disabled={traceabilityPage <= 1 || loadingTraceability}
                      className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg flex items-center gap-1"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      {t.common?.previous || 'Anterior'}
                    </button>
                    <button
                      onClick={() => loadTraceability(traceabilityPage + 1)}
                      disabled={traceabilityPage >= Math.ceil(traceabilityTotal / PAGE_SIZE) || loadingTraceability}
                      className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg flex items-center gap-1"
                    >
                      {t.common?.next || 'Siguiente'}
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}

        {activeTab === 'sala-mezcla' && (
          <Card className="p-6">
            <SalaMezclaGestion factory={filters.factory || defaultFactory} />
          </Card>
        )}
      </div>
    </div>
  )
}
